/**
 * Staff and invitations.
 *
 * Reads come straight from Firestore; every write goes through a callable,
 * because the rules forbid client writes here and because the work is more
 * than setting a field — inviting sends mail, changing roles rewrites custom
 * claims, and removing somebody has to revoke their session.
 */

import { calendarDate } from '../../shared/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { call } from '../../lib/callable';
import { isRole, type Role } from '../../types/roles';
import { sortName, splitName } from '../../types/personName';

const db = getFirestore(app);

export interface Member {
  uid: string;
  email: string;
  displayName: string;
  /** The parts, as the directory keeps them; older records have only the full name. */
  firstName: string;
  lastName: string;
  /** "Reyes, Dana", the roster order. */
  sortName: string;
  roles: Role[];
  title: string | null;
  /** What "who must acknowledge: these departments" matches on. */
  departmentIds: string[];
  status: string;
}

export interface Invite {
  id: string;
  email: string;
  roles: Role[];
  status: string;
  createdAt: string | null;
  expiresAt: string | null;
}

/** A date-only value, read in UTC. One rule, in shared/dates.ts. */
const toIsoDate = calendarDate;

function toRoles(v: unknown): Role[] {
  return Array.isArray(v) ? v.filter(isRole) : [];
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' && v ? v : fallback;
}

export function useMembers(orgId: string | null) {
  return useQuery({
    queryKey: ['members', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<Member[]> => {
      const snap = await getDocs(collection(db, 'orgs', orgId!, 'members'));
      return snap.docs
        .map((doc) => {
          const d = doc.data();
          const email = str(d.email);
          return {
            uid: doc.id,
            email,
            // Falls back to the email so a row is never nameless — a blank
            // first column makes a table unusable.
            displayName: str(d.displayName, email || 'Unknown'),
            firstName: str(d.firstName) || splitName(str(d.displayName)).firstName,
            lastName: str(d.lastName) || splitName(str(d.displayName)).lastName,
            sortName: sortName(str(d.firstName), str(d.lastName), str(d.displayName, email || 'Unknown')),
            roles: toRoles(d.roles),
            title: str(d.title) || null,
            departmentIds: Array.isArray(d.departmentIds) ? d.departmentIds.filter((x): x is string => typeof x === 'string') : [],
            status: str(d.status, 'active'),
          };
        })
        // The roster order: by last name, as HR lists people.
        .sort((a, b) => a.sortName.localeCompare(b.sortName));
    },
  });
}

/** Invitations that have been sent and not yet accepted. */
export function usePendingInvites(orgId: string | null) {
  return useQuery({
    queryKey: ['invites', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<Invite[]> => {
      const snap = await getDocs(
        query(
          collection(db, 'orgs', orgId!, 'invites'),
          where('status', '==', 'pending'),
        ),
      );
      return snap.docs.map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          email: str(d.email),
          roles: toRoles(d.roles),
          status: str(d.status, 'pending'),
          createdAt: toIsoDate(d.createdAt),
          expiresAt: toIsoDate(d.expiresAt),
        };
      });
    },
  });
}

/**
 * What the backend says after an invitation is created or resent. `message`
 * is the honest sentence — "sent", or "created but the email could not go;
 * copy the link" — and `inviteLink` is for that second case.
 */
export interface InviteResult {
  inviteId?: string;
  inviteLink?: string;
  emailSent?: boolean;
  emailVia?: string;
  emailReason?: string | null;
  message?: string;
}

/** Everything that changes the roster invalidates both lists. */
function useRosterMutation<TInput, TResult = unknown>(
  orgId: string | null,
  name: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => call<TInput, TResult>(name, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['invites', orgId] });
    },
  });
}

export function useInviteUser(orgId: string | null) {
  return useRosterMutation<{ email: string; roles: Role[]; title?: string; firstName?: string; lastName?: string; departmentIds?: string[] }, InviteResult>(
    orgId,
    'inviteUser',
  );
}

export function useSetUserRoles(orgId: string | null) {
  return useRosterMutation<{ targetUid: string; roles: Role[] }>(orgId, 'setUserRoles');
}

/**
 * What a policy still pointing at a departed member looks like on the screen
 * that says so. Ownership is never moved automatically; this is the telling.
 */
export interface LeftBehind {
  policyId: string;
  policyNumber: string;
  title: string;
  status: string;
  as: 'owner' | 'co-owner';
}

export interface RemovalResult {
  userId: string;
  email: string | null;
  message: string;
  /** Active policies still assigned to them, for the administrator to hand on. */
  leftBehind: LeftBehind[];
  /** Requirements to read a policy that were closed because they can no longer meet them. */
  requirementsClosed: number | null;
}

export function useRemoveUser(orgId: string | null) {
  // The backend reads `userId`; sending `targetUid` made Remove fail every time.
  return useRosterMutation<{ userId: string }, RemovalResult>(orgId, 'removeUser');
}

/**
 * One person's membership story, from the audit log that already holds it.
 * Read-only, and only the events that are part of a membership.
 */
export interface MemberHistoryEvent {
  id: string;
  what: string;
  at: string | null;
  byName: string | null;
  byEmail: string | null;
  detail: string | null;
}

export interface MemberHistory {
  uid: string;
  email: string | null;
  displayName: string | null;
  status: string | null;
  roles: string[];
  joinedAt: string | null;
  removedAt: string | null;
  events: MemberHistoryEvent[];
  truncated: boolean;
}

export function useMemberHistory(orgId: string | null, uid: string | null) {
  return useQuery({
    queryKey: ['member-history', orgId, uid],
    enabled: Boolean(orgId && uid),
    retry: false,
    staleTime: 30_000,
    queryFn: () => call<{ uid: string }, MemberHistory>('getMemberHistory', { uid: uid! }),
  });
}

export function useCancelInvite(orgId: string | null) {
  return useRosterMutation<{ inviteId: string }>(orgId, 'cancelInvite');
}

export function useResendInvite(orgId: string | null) {
  return useRosterMutation<{ inviteId: string }, InviteResult>(orgId, 'resendInvite');
}

/** Name, title and departments. Own record when targetUid is omitted. */
export function useUpdateMemberProfile(orgId: string | null) {
  return useRosterMutation<{ targetUid?: string; firstName?: string; lastName?: string; displayName?: string; title?: string; departmentIds?: string[] }, { displayName: string | null }>(
    orgId,
    'updateMemberProfile',
  );
}

export type BulkInviteRow = { email: string; roles: Role[]; firstName?: string; lastName?: string; title?: string; departmentIds?: string[] };
export type BulkInviteRowResult =
  | { email: string; status: 'already_member' }
  | { email: string; status: 'already_invited'; inviteId: string }
  | { email: string; status: 'failed'; reason: string }
  | { email: string; status: 'invited'; inviteId: string; inviteLink: string; emailSent: boolean; emailReason: string | null; message: string };
export interface BulkInviteResult { results: BulkInviteRowResult[]; invited: number; emailFailures: number }

/** A spreadsheet's worth of invitations, each with its own outcome. */
export function useInviteUsersBulk(orgId: string | null) {
  return useRosterMutation<{ rows: BulkInviteRow[] }, BulkInviteResult>(orgId, 'inviteUsersBulk');
}
